import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiPlus, FiList } from 'react-icons/fi';
import { format } from 'date-fns';
import usePendingPurchases from '../hooks/usePendingPurchases';
import { deletePendingPurchase } from '../api';
import CancelPendingSaleModal from './CancelPendingSaleModal';
import InlineLoading from './InlineLoading';
import { notify } from '../utils/alertBus';
import { filterPendingByRegisterScope } from '../utils/pendingPurchases';

const PendingSalesTabs = ({
  branchId,
  activePurchaseId,
  onSelect,
  cashRegisterId,
  cashRegisterSessionId,
  maxOpenSales,
  enableRealtime = true,
  tone = 'neutral',
  canDelete = true,
  canCreate = true,
  canSelect = true,
  scopeReady = true,
  activePurchaseLiveId = null,
  activePurchaseLiveTotal = null,
}) => {
  const { pending, loading, error, reload } = usePendingPurchases(branchId, {
    enableRealtime,
  });
  const [showAll, setShowAll] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const allMenuRef = useRef(null);
  const toneStyles =
    tone === 'navbar'
      ? {
          activeTab: 'bg-gray-800/90 border-gray-800 text-white',
          activeMeta: 'text-gray-200 group-hover:text-white',
          activeDelete: 'text-gray-200 hover:text-white',
          activeNewButton: 'bg-gray-800/90 border-gray-800 text-white',
        }
      : tone === 'blue'
        ? {
            activeTab: 'bg-blue-600 border-blue-700 text-white',
            activeMeta: 'text-blue-100 group-hover:text-white',
            activeDelete: 'text-blue-100 hover:text-white',
            activeNewButton: 'bg-blue-600 border-blue-700 text-white',
          }
        : {
            activeTab: 'bg-white border-blue-400 text-blue-700',
            activeMeta: 'text-gray-500 group-hover:text-gray-600',
            activeDelete: 'text-gray-400 hover:text-red-600',
            activeNewButton: 'bg-blue-500 border-blue-500 text-white',
          };

  const handleSelect = (idOrNull) => {
    if (idOrNull && !canSelect) return;
    if (!idOrNull && !canCreate) return;
    if (typeof onSelect === 'function') {
      onSelect(idOrNull);
    }
  };

  const handleDelete = (purchase) => {
    if (!canDelete) return;
    if (!branchId || !purchase?.id) return;
    setCancelTarget(purchase);
  };

  const handleConfirmDelete = async (cancelPayload) => {
    if (!canDelete) return;
    if (!branchId || !cancelTarget?.id) {
      throw new Error('No se pudo identificar la venta.');
    }
    try {
      await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
      if (cancelTarget.id === activePurchaseId) {
        handleSelect(null);
      }
      await reload();
    } catch (err) {
      console.error('Error deleting pending purchase:', err);
      throw new Error(
        err?.response?.data?.message || 'No se pudo eliminar la venta pendiente.'
      );
    }
  };

  const formatLabel = (purchase) => {
    const clientName = purchase.Client?.name || 'Sin cliente';
    const total =
      typeof purchase.totalAmount === 'number'
        ? `$${purchase.totalAmount.toLocaleString('de-DE')}`
        : '';

    let time = '';
    if (purchase.createdAt) {
      const created = new Date(purchase.createdAt);
      if (!Number.isNaN(created.getTime())) {
        time = format(created, 'HH:mm');
      }
    }

    return [clientName, total || null, time || null]
      .filter(Boolean)
      .join(' · ');
  };

  useEffect(() => {
    if (!showAll) return;
    const handleClick = (event) => {
      if (allMenuRef.current && !allMenuRef.current.contains(event.target)) {
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showAll]);

  const isNewActive = !activePurchaseId;
  const parsedMaxOpenSales = Number.isInteger(maxOpenSales) ? maxOpenSales : 5;
  const limitApplies =
    (cashRegisterSessionId !== null && cashRegisterSessionId !== undefined)
    || (cashRegisterId !== null && cashRegisterId !== undefined);
  const pendingByStatus = useMemo(
    () =>
      (pending || []).filter((purchase) =>
        purchase?.saleStatus === 'open' || purchase?.saleStatus === 'suspended'
      ),
    [pending]
  );

  const scopedPending = useMemo(
    () => {
      if (!scopeReady) return [];
      return filterPendingByRegisterScope(pendingByStatus, {
        cashRegisterSessionId,
        cashRegisterId,
      });
    },
    [pendingByStatus, cashRegisterSessionId, cashRegisterId, scopeReady]
  );
  const displayPending = useMemo(() => {
    const livePurchaseId = activePurchaseLiveId == null ? null : String(activePurchaseLiveId);
    const liveTotal = Number(activePurchaseLiveTotal);

    return scopedPending.map((purchase) => {
      return {
        ...purchase,
        ...(
          livePurchaseId !== null
          && String(purchase?.id) === livePurchaseId
          && Number.isFinite(liveTotal)
            ? { totalAmount: liveTotal }
            : {}
        ),
      };
    });
  }, [activePurchaseLiveId, activePurchaseLiveTotal, scopedPending]);
  const openSales = displayPending.filter((purchase) => purchase.saleStatus === 'open');
  const shouldBlockNewSale =
    limitApplies && !isNewActive && openSales.length >= parsedMaxOpenSales;
  const newSaleBlockedMessage = `Se alcanzó el límite de ventas pendientes (${parsedMaxOpenSales}) para esta caja.`;

  const handleNewSaleClick = () => {
    if (shouldBlockNewSale) {
      notify(newSaleBlockedMessage);
      return;
    }
    handleSelect(null);
  };

  const buildTabTitle = (purchase) => {
    const type = (purchase?.documentType || '').toLowerCase();
    const typeLabel = type === 'boleta' || type === 'factura'
      ? `${type.charAt(0).toUpperCase()}${type.slice(1)} `
      : '';
    return `${typeLabel}#${purchase.id}`;
  };

  return (
    <>
      <div className="py-2 sticky top-14 z-10 w-full bg-gray-100 border-b border-gray-200 text-xs sm:text-sm text-gray-800">
        <div className="flex items-start justify-between px-1.5 py-1">
          <div className="flex-1 min-w-0 overflow-x-auto">
            <div className="flex items-end gap-1 flex-nowrap w-max">
              {displayPending.map((purchase) => {
                const isActive = activePurchaseId === purchase.id;
                return (
                  <div
                    key={purchase.id}
                    className={`group flex items-center max-w-xs border border-b-0 rounded-t-md whitespace-nowrap ${
                      isActive
                        ? toneStyles.activeTab
                        : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-white hover:text-gray-800'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(purchase.id)}
                      className="px-3 py-1 text-left truncate"
                    >
                      <span className="font-medium">{buildTabTitle(purchase)}</span>
                      <span
                        className={`ml-1 ${
                          isActive
                            ? toneStyles.activeMeta
                            : 'text-gray-500 group-hover:text-gray-600'
                        }`}
                      >
                        · {formatLabel(purchase)}
                      </span>
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(purchase)}
                        className={`px-1.5 py-1 flex items-center ${
                          isActive
                            ? toneStyles.activeDelete
                            : 'text-gray-400 hover:text-red-600'
                        }`}
                      >
                        <FiX size={11} />
                      </button>
                    )}
                  </div>
                );
              })}

              {loading && (
                <span className="text-gray-400 whitespace-nowrap px-2 py-1">
                  <InlineLoading />
                </span>
              )}

              {!scopeReady && (
                <span className="text-gray-400 whitespace-nowrap px-2 py-1">
                  <InlineLoading label="Cargando pendientes" />
                </span>
              )}

              {error && !loading && (
                <span className="text-red-500 whitespace-nowrap px-2 py-1">
                  Error al cargar
                </span>
              )}
            </div>
          </div>

          <div className="relative flex items-start gap-1 flex-shrink-0 ml-2 self-start">
            {canCreate && (
              <button
                type="button"
                onClick={handleNewSaleClick}
                title={shouldBlockNewSale ? newSaleBlockedMessage : 'Nueva venta'}
                className={`inline-flex items-center justify-center w-7 h-7 border rounded-md ${
                  isNewActive
                    ? toneStyles.activeNewButton
                    : shouldBlockNewSale
                      ? 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
                aria-disabled={shouldBlockNewSale}
              >
                <FiPlus size={14} />
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              title="Ver pendientes"
              className="inline-flex items-center justify-center w-7 h-7 border border-gray-300 rounded-md bg-white text-gray-600 hover:bg-gray-100"
            >
              <FiList size={14} />
            </button>

            {showAll && (
              <div
                ref={allMenuRef}
                className="absolute right-0 top-full mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-50"
              >
                <div className="px-3 py-2 border-b text-xs font-semibold text-gray-700">
                  Pendientes
                </div>
                {displayPending.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    No hay pendientes hoy.
                  </div>
                ) : (
                  <ul className="py-1">
                    {displayPending.map((purchase) => (
                      <li key={purchase.id}>
                        <button
                          type="button"
                          onClick={() => {
                            handleSelect(purchase.id);
                            setShowAll(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 ${
                            activePurchaseId === purchase.id
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700'
                          }`}
                        >
                          <div className="font-medium">
                            {buildTabTitle(purchase)}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {formatLabel(purchase)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <CancelPendingSaleModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        saleLabel={cancelTarget ? buildTabTitle(cancelTarget) : ''}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default PendingSalesTabs;
